import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, UserPlus, Key, CheckCircle, AlertCircle } from 'lucide-react';
import Button from '../common/Button';
import { createAdminAccounts, DEFAULT_ADMIN_CREDENTIALS } from '../../utils/setupAdminAuth';

interface AdminSetupProps {
  onSetupComplete: () => void;
}

const AdminSetup: React.FC<AdminSetupProps> = ({ onSetupComplete }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [setupResults, setSetupResults] = useState<any[]>([]);
  const [showCredentials, setShowCredentials] = useState(false);

  const handleCreateAccounts = async () => {
    setIsCreating(true);
    try {
      const results = await createAdminAccounts();
      setSetupResults(results);
      setShowCredentials(true);
    } catch (error) {
      console.error('Setup failed:', error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-2xl"
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="flex items-center justify-center mb-6"
            >
              <div className="bg-primary-100 p-4 rounded-full">
                <Shield className="h-12 w-12 text-primary-600" />
              </div>
            </motion.div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Admin Setup Required
            </h1>
            
            <p className="text-gray-600">
              Create admin accounts for the Export Readiness Test dashboard
            </p>
          </div>

          {!showCredentials ? (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <UserPlus className="w-6 h-6 text-blue-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-blue-800 mb-2">Admin Accounts to Create</h3>
                    <ul className="space-y-2 text-blue-700">
                      <li>• admin@lsem.org.ng</li>
                      <li>• info@lsem.org.ng</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <Key className="w-6 h-6 text-amber-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-amber-800 mb-2">Default Passwords</h3>
                    <p className="text-amber-700 text-sm">
                      Default passwords will be set. You can change them later in the Firebase Console 
                      or by implementing a password reset feature.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                variant="primary"
                size="lg"
                isFullWidth
                leftIcon={<UserPlus />}
                onClick={handleCreateAccounts}
                disabled={isCreating}
              >
                {isCreating ? 'Creating Admin Accounts...' : 'Create Admin Accounts'}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <h3 className="font-semibold text-green-800">Setup Complete!</h3>
                </div>
                
                <div className="space-y-3">
                  {setupResults.map((result, index) => (
                    <div key={index} className="flex items-center gap-3">
                      {result.success ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-600" />
                      )}
                      <span className={result.success ? 'text-green-700' : 'text-red-700'}>
                        {result.email} - {result.success ? 'Created successfully' : result.error}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-semibold text-blue-800 mb-3">Login Credentials</h3>
                <div className="space-y-3 font-mono text-sm">
                  {Object.entries(DEFAULT_ADMIN_CREDENTIALS).map(([email, password]) => (
                    <div key={email} className="bg-white p-3 rounded border">
                      <div className="text-blue-700">Email: {email}</div>
                      <div className="text-blue-700">Password: {password}</div>
                    </div>
                  ))}
                </div>
                <p className="text-blue-600 text-xs mt-3">
                  💡 Save these credentials securely. You can change passwords later in Firebase Console.
                </p>
              </div>

              <Button
                variant="primary"
                size="lg"
                isFullWidth
                onClick={onSetupComplete}
              >
                Continue to Admin Login
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AdminSetup;